import { inject, Injectable } from '@angular/core';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import { Observable } from 'rxjs';
import {
  AiLegalMapAcceptItem,
  AiLegalMapInfo,
  AiLegalMapProgress,
  AiLegalMapResult,
  AiLegalMapStartResponse,
  LegalMapMapping,
  LegalMapStampedPdfResponse
} from '../models/legal-map.models';

@Injectable({
  providedIn: 'root'
})
export class LegalMapService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  // keepMappings = replace the PDF with a revision of the same plan; existing lot pins are kept.
  assignLegalPdf(projectId: string, legalMapDocumentId: string, keepMappings = false): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/project/${projectId}/legal-map?legalMapDocumentId=${legalMapDocumentId}&keepMappings=${keepMappings}`,
      {},
      { showLoader: true, showMessage: true }
    );
  }

  getLegalMapMappings(projectId: string): Observable<LegalMapMapping[]> {
    return this.#http.getData(`${this.baseUrl}/project/${projectId}/legal-map/mappings`, {
      showLoader: true,
      showMessage: false
    });
  }

  deleteAllLegalMapMappings(projectId: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/project/${projectId}/legal-map/mappings`, {
      showLoader: true,
      showMessage: true,
      message: 'Legal PDF mappings cleared successfully'
    });
  }

  getMappedLotsCount(projectId: string): Observable<number> {
    return this.#http.getData(`${this.baseUrl}/project/${projectId}/legal-map/lots/count-mapped`, {
      showLoader: false,
      showMessage: false
    });
  }

  getUnmappedLotsCount(projectId: string): Observable<number> {
    return this.#http.getData(`${this.baseUrl}/project/${projectId}/legal-map/lots/count-unmapped`, {
      showLoader: false,
      showMessage: false
    });
  }

  getAiMapInfo(projectId: string): Observable<AiLegalMapInfo> {
    return this.#http.getData(`${this.baseUrl}/legalcoordinatemapping/projects/${projectId}/ai-map/info`, {
      showLoader: false,
      showMessage: false
    });
  }

  runAiMap(projectId: string): Observable<AiLegalMapStartResponse> {
    return this.#http.postData(
      `${this.baseUrl}/legalcoordinatemapping/projects/${projectId}/ai-map`,
      { showLoader: false, showMessage: false },
      {}
    );
  }

  cancelAiMap(runId: string): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/legalcoordinatemapping/ai-map/runs/${runId}/cancel`,
      { showLoader: false, showMessage: false },
      {}
    );
  }

  getAiMapProgress(projectId: string): Observable<AiLegalMapProgress> {
    return this.#http.getData(`${this.baseUrl}/legalcoordinatemapping/projects/${projectId}/ai-map/progress`, {
      showLoader: false,
      showMessage: false
    });
  }

  acceptAiMapProposals(projectId: string, items: AiLegalMapAcceptItem[]): Observable<number> {
    return this.#http.postData(
      `${this.baseUrl}/legalcoordinatemapping/projects/${projectId}/ai-map/accept`,
      { showLoader: true, showMessage: true, message: 'AI mappings saved successfully' },
      { items }
    );
  }

  upsertLegalCoordinateMapping(
    lotId: string,
    coords: { pdfX: number; pdfY: number; pdfPageWidth: number; pdfPageHeight: number; pageNumber: number }
  ): Observable<LegalMapMapping> {
    return this.#http.putData(`${this.baseUrl}/legalcoordinatemapping/lots/${lotId}`, coords, {
      showLoader: true,
      showMessage: true,
      message: 'Lot mapped successfully'
    });
  }

  getLegalCoordinateMappingByLotId(lotId: string): Observable<LegalMapMapping> {
    return this.#http.getData(`${this.baseUrl}/legalcoordinatemapping/lots/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  deleteLegalCoordinateMapping(lotId: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/legalcoordinatemapping/lots/${lotId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Mapping deleted successfully'
    });
  }

  generateStampedPdf(lotId: string): Observable<LegalMapStampedPdfResponse> {
    return this.#http.postData(
      `${this.baseUrl}/legalcoordinatemapping/lots/${lotId}/stamped-pdf`,
      { showLoader: true, showMessage: false },
      {}
    );
  }
}
